import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Download, X, Clock, CheckCircle2 } from "lucide-react";
import { api, AYLAR, formatNum, fmtDate } from "@/lib/api";
import { saveBlob } from "@/components/payroll/bankUtils";

export function PrimStatementModal({ yil: y0, ay: m0, personnelId, locked, onClose }) {
  const [yil, setYil] = useState(y0);
  const [ay, setAy] = useState(m0);
  const [data, setData] = useState(null);
  const params = { yil, ay, ...(personnelId ? { personnel_id: personnelId } : {}) };

  useEffect(() => {
    setData(null);
    api.get("/prim-statement", { params }).then((r) => setData(r.data))
      .catch((e) => { toast.error(e?.response?.data?.detail || "Ekstre yüklenemedi"); setData(false); });
    // eslint-disable-next-line
  }, [yil, ay, personnelId]);

  const pdf = async () => {
    try { saveBlob(await api.get("/prim-statement/pdf", { params, responseType: "blob" }), `Prim Ekstresi ${yil}-${ay}.pdf`); }
    catch { toast.error("PDF indirilemedi"); }
  };

  const now = new Date().getFullYear();
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="prim-statement-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 flex-wrap">
          <FileText size={18} className="text-indigo-600" />
          <h3 className="font-display font-bold text-slate-900">Prim Ekstresi</h3>
          {data && <span className="text-xs text-slate-500">{data.personnel.ad_soyad}</span>}
          {locked ? (
            <span className="ml-auto text-xs font-semibold text-slate-600 bg-slate-100 rounded-md px-2 py-1" data-testid="prim-statement-locked-period">
              {AYLAR.find((a) => a.v === ay)?.n} {yil}
            </span>
          ) : (
            <>
              <select value={ay} onChange={(e) => setAy(Number(e.target.value))} data-testid="prim-statement-month" className="ml-auto px-2 py-1 border border-slate-300 rounded-md text-xs bg-white">
                {AYLAR.map((a) => <option key={a.v} value={a.v}>{a.n}</option>)}
              </select>
              <select value={yil} onChange={(e) => setYil(Number(e.target.value))} data-testid="prim-statement-year" className="px-2 py-1 border border-slate-300 rounded-md text-xs bg-white">
                {[now - 1, now].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </>
          )}
          <button onClick={pdf} disabled={!data} data-testid="prim-statement-pdf" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold disabled:opacity-40">
            <Download size={12} /> PDF
          </button>
          <button onClick={onClose} data-testid="prim-statement-close" className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-5 text-sm">
          {data === null && <div className="text-xs text-slate-400">Yükleniyor...</div>}
          {data && (
            <>
              <div className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px] rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="prim-statement-approved-total">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{personnelId ? "Onaylanan pay" : "Onaylanan payınız"} · {data.donem}</div>
                  <div className="text-2xl font-extrabold text-emerald-800 font-mono-tab">{formatNum(data.approved_total)} ₺</div>
                  <div className="text-xs text-emerald-700">{data.approved.length} prim</div>
                </div>
                <div className="flex-1 min-w-[180px] rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="prim-statement-pending-count">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Onay bekleyen</div>
                  <div className="text-2xl font-extrabold text-amber-800">{data.pending.length} prim</div>
                  <div className="text-xs text-amber-700">Tutarı onaydan sonra görünür</div>
                </div>
              </div>
              <Section personnelId={personnelId} title="Onaylanan Primler" Icon={CheckCircle2} tone="text-emerald-700" rows={data.approved} amount testid="prim-statement-approved" />
              <Section personnelId={personnelId} title="Onay Bekleyen Primler" Icon={Clock} tone="text-amber-700" rows={data.pending} testid="prim-statement-pending" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, Icon, tone, rows, amount, testid, personnelId }) {
  return (
    <div data-testid={testid}>
      <div className={`text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1 ${tone}`}><Icon size={13} /> {title} ({rows.length})</div>
      {!rows.length ? <div className="text-xs italic text-slate-400">Bu dönemde kayıt yok.</div> : (
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-slate-500 bg-slate-50">
            <tr><th className="text-left p-1.5">Tarih</th><th className="text-left p-1.5">Föy</th><th className="text-left p-1.5">Dosya</th><th className="text-left p-1.5">Müvekkil</th><th className="text-left p-1.5">Rol</th><th className="text-right p-1.5">{amount ? (personnelId ? "Pay" : "Payınız") : "Durum"}</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`${testid}-row-${r.id}`}>
                <td className="p-1.5 whitespace-nowrap">{fmtDate(r.tarih)}</td>
                <td className="p-1.5">{r.icra_foy}</td>
                <td className="p-1.5 font-semibold text-slate-800">{r.dosya_adi}</td>
                <td className="p-1.5 text-slate-600">{r.alacakli_adi}</td>
                <td className="p-1.5">{r.rol}</td>
                <td className="p-1.5 text-right font-mono-tab font-bold">{amount ? `${formatNum(r.pay)} ₺` : <span className="text-amber-700 font-semibold">Onay bekliyor</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
